import { useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiDownload, FiTrash2, FiShield, FiAlertTriangle } from 'react-icons/fi'
import { useAuth } from '../../contexts/AuthContext'

export default function MisDatos() {
  const { empleado, logout } = useAuth()
  const [exporting, setExporting] = useState(false)
  const [showConfirmar, setShowConfirmar] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)

  const handleExport = async () => {
    setExporting(true)
    try {
      const res = await axios.get('/api/datos-personales/export', { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `presenzia-mis-datos-${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Tus datos se han descargado en formato JSON')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al exportar tus datos')
    } finally {
      setExporting(false)
    }
  }

  const handleEliminar = async () => {
    if (confirmText !== 'ELIMINAR') {
      toast.error('Debes escribir ELIMINAR para confirmar')
      return
    }
    setDeleting(true)
    try {
      const res = await axios.post('/api/datos-personales/eliminar', { confirm: 'ELIMINAR' })
      toast.success(res.data.mensaje || 'Cuenta anonimizada')
      setTimeout(() => logout?.(), 1500)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al procesar la solicitud')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <header className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold uppercase tracking-wider mb-3">
          <FiShield className="h-3.5 w-3.5" /> Tus derechos RGPD
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Mis datos personales</h1>
        <p className="text-gray-600">
          Conforme al Reglamento General de Protección de Datos puedes acceder a una copia de
          todos los datos que tenemos sobre ti y solicitar su eliminación. Más detalles en
          nuestra <Link to="/privacidad" target="_blank" className="text-blue-600 hover:underline">política de privacidad</Link>.
        </p>
      </header>

      {/* Acceso + portabilidad */}
      <section className="mb-6 p-6 rounded-2xl border border-gray-200 bg-white">
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-blue-100 grid place-items-center">
            <FiDownload className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Descargar mis datos (JSON)</h2>
            <p className="text-sm text-gray-600 mb-4">
              Recibe una copia completa de tus datos personales en formato estructurado: perfil,
              empresa, fichajes, ausencias, correcciones y sesiones recientes. Cubre los derechos
              de acceso (art. 15 RGPD) y portabilidad (art. 20 RGPD).
            </p>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
            >
              <FiDownload className="w-4 h-4" />
              {exporting ? 'Exportando…' : 'Descargar mi archivo JSON'}
            </button>
          </div>
        </div>
      </section>

      {/* Supresión */}
      <section className="p-6 rounded-2xl border border-red-200 bg-red-50/30">
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-red-100 grid place-items-center">
            <FiTrash2 className="w-5 h-5 text-red-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Eliminar mi cuenta</h2>
            <p className="text-sm text-gray-600 mb-3">
              Anonimizamos tu nombre, email, teléfono, DNI y contraseña. Los registros de
              fichaje permanecen sin tu identidad por obligación legal de conservación durante
              4 años (art. 34.9 ET).
            </p>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 mb-4">
              <FiAlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <strong>Esta acción es irreversible.</strong> Perderás el acceso a la
                aplicación inmediatamente. Si eres administrador de la empresa, antes debes
                asignar otro administrador.
              </div>
            </div>

            {!showConfirmar ? (
              <button
                type="button"
                onClick={() => setShowConfirmar(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-300 text-red-700 text-sm font-semibold hover:bg-red-100"
              >
                <FiTrash2 className="w-4 h-4" />
                Solicitar eliminación
              </button>
            ) : (
              <div className="space-y-3">
                <label className="block">
                  <span className="text-sm text-gray-700">
                    Escribe <strong className="font-mono">ELIMINAR</strong> para confirmar:
                  </span>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="ELIMINAR"
                    autoComplete="off"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
                  />
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowConfirmar(false); setConfirmText('') }}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleEliminar}
                    disabled={deleting || confirmText !== 'ELIMINAR'}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
                  >
                    {deleting ? 'Procesando…' : 'Confirmar eliminación'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <p className="text-xs text-gray-500 mt-6 text-center">
        ¿Otras dudas sobre tus datos? Escribe a{' '}
        <a href="mailto:privacidad@presenzia.app" className="text-blue-600 hover:underline">
          privacidad@presenzia.app
        </a>
        {empleado?.email ? <> (cuenta: <span className="font-mono">{empleado.email}</span>)</> : null}.
      </p>
    </div>
  )
}
